import { v4 as uuidv4 } from 'uuid';
import { Student, Program } from './types';
import { universalStorage } from '../storage/universalStorage';
import dbAdapter from './adapter';
import { logger } from '@/utils/logging';
import { mediaStorage } from '../storage/mediaStorage';

export class StudentService {
  async getAllStudents(): Promise<Student[]> {
    try {
      const students = await universalStorage.getItem<Student[]>('nalc_students');
      
      // Validate and restore student profile images with enhanced persistence
      if (students && Array.isArray(students)) {
        const validatedStudents = await Promise.all(students.map(async (student) => {
          const validatedImage = await this.validateAndRestoreProfileImage(student.id, student.profileImage, student.name);
          return {
            ...student,
            profileImage: validatedImage
          };
        }));
        
        // Save back the validated data only if changes were made
        const hasChanges = validatedStudents.some((student, index) => 
          student.profileImage !== students[index].profileImage
        );
        
        if (hasChanges) {
          await universalStorage.setItem('nalc_students', validatedStudents);
          logger.studentService.info('Restored and saved profile images for enhanced persistence');
        }
        
        return validatedStudents;
      }
      
      return students || [];
    } catch (error) {
      logger.studentService.error('Failed to get all students', undefined, error as Error);
      return [];
    }
  }

  private async validateAndRestoreProfileImage(studentId: number, profileImage?: string, studentName?: string): Promise<string | undefined> {
    if (!profileImage) {
      // Try to restore from media storage
      const restoredImage = await mediaStorage.getStudentProfileImage(studentId);
      if (restoredImage) {
        logger.studentService.info(`Restored profile image for student: ${studentName} (ID: ${studentId})`);
        return restoredImage;
      }
      return undefined;
    }
    
    // Enhanced validation for PWA environments
    try {
      // Check if it's a valid data URL with proper format
      if (profileImage.startsWith('data:image/')) {
        const [header, base64Data] = profileImage.split(',');
        
        if (!base64Data || base64Data.length === 0) {
          logger.studentService.warn(`Invalid base64 data for student: ${studentName}, attempting restore`);
          return await mediaStorage.getStudentProfileImage(studentId);
        }
        
        // Validate mime type
        const mimeMatch = header.match(/data:image\/(jpeg|jpg|png|gif|webp);base64/);
        if (!mimeMatch) {
          logger.studentService.warn(`Invalid image mime type for student: ${studentName}`, { header });
          return await mediaStorage.getStudentProfileImage(studentId);
        }
        
        // Test base64 validity
        try {
          atob(base64Data);
          // Save to media storage for better persistence
          await mediaStorage.saveStudentProfileImage(studentId, profileImage);
          return profileImage;
        } catch (e) {
          logger.studentService.warn(`Corrupted base64 data for student: ${studentName}, attempting restore`);
          return await mediaStorage.getStudentProfileImage(studentId);
        }
      }
      
      // Check if it's a valid blob URL (may not persist across sessions)
      if (profileImage.startsWith('blob:')) {
        logger.studentService.warn(`Blob URL detected for student: ${studentName} - attempting to restore from storage`);
        const restoredImage = await mediaStorage.getStudentProfileImage(studentId);
        return restoredImage || profileImage;
      }
      
      // Check if it's a valid HTTP/HTTPS URL
      if (profileImage.startsWith('http://') || profileImage.startsWith('https://')) {
        try {
          new URL(profileImage);
          return profileImage;
        } catch (e) {
          logger.studentService.warn(`Invalid URL for student: ${studentName}`, { profileImage });
          return await mediaStorage.getStudentProfileImage(studentId);
        }
      }
      
      // Check if it's a valid relative path
      if (profileImage.startsWith('/') || profileImage.startsWith('./')) {
        return profileImage;
      }
      
      // If none of the above, try to restore from storage
      logger.studentService.warn(`Invalid profile image format for student: ${studentName}, attempting restore`);
      return await mediaStorage.getStudentProfileImage(studentId);
      
    } catch (error) {
      logger.studentService.error(`Error validating profile image for student: ${studentName}`, undefined, error as Error);
      // Try to restore from media storage as fallback
      return await mediaStorage.getStudentProfileImage(studentId);
    }
  }

  async getStudentById(id: number): Promise<Student | undefined> {
    try {
      // First try to get from individual file if using desktop storage
      if (universalStorage.isDesktopStorageActive()) {
        const student = await universalStorage.getItem<Student>(`nalc_students:${id}`);
        if (student) {
          return {
            ...student,
            profileImage: await this.validateAndRestoreProfileImage(student.id, student.profileImage, student.name)
          };
        }
      }
      
      // Fallback to searching in main students list
      const students = await this.getAllStudents();
      const student = students.find(student => student.id === id);
      
      if (student) {
        return {
          ...student,
          profileImage: await this.validateAndRestoreProfileImage(student.id, student.profileImage, student.name)
        };
      }
      
      return undefined;
    } catch (error) {
      logger.studentService.error(`Failed to get student ${id}`, undefined, error as Error);
      return undefined;
    }
  }

  async addStudent(student: Omit<Student, 'id' | 'createdAt' | 'updatedAt'>): Promise<Student> {
    try {
      const students = await this.getAllStudents();
      const maxId = students.reduce((max, s) => Math.max(max, s.id), 0);
      
      const newStudent: Student = {
        ...student,
        id: maxId + 1,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Enhanced profile image handling with persistence
      if (student.profileImage) {
        try {
          const savedImage = await mediaStorage.saveStudentProfileImage(newStudent.id, student.profileImage);
          newStudent.profileImage = savedImage;
          logger.studentService.info('Saved profile image with enhanced persistence', {
            name: newStudent.name,
            id: newStudent.id,
            imageSize: savedImage.length
          });
        } catch (error) {
          logger.studentService.warn('Failed to save profile image to media storage, keeping original', {
            name: newStudent.name,
            id: newStudent.id
          });
          newStudent.profileImage = student.profileImage;
        }
      }
      
      // Add to main students list
      students.push(newStudent);
      await universalStorage.setItem('nalc_students', students);
      
      // Save individual student file if using desktop storage
      if (universalStorage.isDesktopStorageActive()) {
        await universalStorage.setItem(`nalc_students:${newStudent.id}`, newStudent);
        
        // Update keys
        const keys = await this.getAllStudentKeys();
        keys.push(newStudent.id.toString());
        await universalStorage.setItem('nalc_student_keys', keys);
      }
      
      // Trigger real-time update
      this.notifyStudentDataChange();
      
      return newStudent;
    } catch (error) {
      logger.studentService.error('Failed to add student', undefined, error as Error);
      throw error;
    }
  }

  async updateStudent(id: number, student: Partial<Omit<Student, 'id' | 'createdAt'>>): Promise<Student> {
    try {
      const students = await this.getAllStudents();
      const index = students.findIndex(s => s.id === id);
      
      if (index === -1) {
        throw new Error(`Student with ID ${id} not found`);
      }
      
      const updatedStudent = {
        ...students[index],
        ...student,
        updatedAt: new Date().toISOString(),
      };

      // Enhanced profile image handling with persistence
      if (student.profileImage) {
        try {
          const savedImage = await mediaStorage.saveStudentProfileImage(id, student.profileImage);
          updatedStudent.profileImage = savedImage;
          logger.studentService.info('Updated profile image with enhanced persistence', {
            id: id,
            name: updatedStudent.name,
            imageSize: savedImage.length
          });
        } catch (error) {
          logger.studentService.warn('Failed to save updated profile image to media storage, keeping original', {
            id: id,
            name: updatedStudent.name
          });
          updatedStudent.profileImage = student.profileImage;
        }
      } else if (!student.profileImage && students[index].profileImage) {
        // Try to restore existing image if not provided in update
        const restoredImage = await mediaStorage.getStudentProfileImage(id);
        if (restoredImage) {
          updatedStudent.profileImage = restoredImage;
        }
      }
      
      // Update main students list
      students[index] = updatedStudent;
      await universalStorage.setItem('nalc_students', students);
      
      // Update individual student file if using desktop storage
      if (universalStorage.isDesktopStorageActive()) {
        await universalStorage.setItem(`nalc_students:${id}`, updatedStudent);
      }
      
      // Trigger real-time update
      this.notifyStudentDataChange();
      
      return updatedStudent;
    } catch (error) {
      logger.studentService.error(`Failed to update student ${id}`, undefined, error as Error);
      throw error;
    }
  }

  async deleteStudent(id: number): Promise<void> {
    try {
      const students = await this.getAllStudents();
      const index = students.findIndex(s => s.id === id);
      
      if (index === -1) {
        throw new Error(`Student with ID ${id} not found`);
      }
      
      // Remove from main students list
      const filteredStudents = students.filter(s => s.id !== id);
      await universalStorage.setItem('nalc_students', filteredStudents);
      
      // Remove individual student file if using desktop storage
      if (universalStorage.isDesktopStorageActive()) {
        await universalStorage.removeItem(`nalc_students:${id}`);
        
        // Update keys
        const keys = await this.getAllStudentKeys();
        const updatedKeys = keys.filter(key => key !== id.toString());
        await universalStorage.setItem('nalc_student_keys', updatedKeys);
      }
      
      // Trigger real-time update
      this.notifyStudentDataChange();
    } catch (error) {
      logger.studentService.error(`Failed to delete student ${id}`, undefined, error as Error);
      throw error;
    }
  }

  private notifyStudentDataChange() {
    // Dispatch custom event for real-time updates
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('studentDataChanged', {
        detail: { timestamp: new Date().toISOString() }
      }));
    }
  }
  
  async searchStudents(query: string): Promise<Student[]> {
    try {
      const students = await this.getAllStudents();
      const searchLower = query.toLowerCase();
      
      return students.filter(student => {
        // Search in student name
        if (student.name.toLowerCase().includes(searchLower)) {
          return true;
        }
        
        // Search in programs
        return student.programs.some(program => {
          return (
            program.title.toLowerCase().includes(searchLower) ||
            program.category.toLowerCase().includes(searchLower)
          );
        });
      });
    } catch (error) {
      logger.studentService.error('Failed to search students', undefined, error as Error);
      return [];
    }
  }
  
  async getUpcomingCompletions(days: number): Promise<{student: Student, program: Program}[]> {
    try {
      const students = await this.getAllStudents();
      const now = new Date();
      const future = new Date();
      future.setDate(future.getDate() + days);
      
      const results: {student: Student, program: Program}[] = [];
      
      students.forEach(student => {
        student.programs.forEach(program => {
          const completionDate = new Date(program.completionDate);
          if (completionDate >= now && completionDate <= future) {
            results.push({
              student,
              program,
            });
          }
        });
      });
      
      return results;
    } catch (error) {
      logger.studentService.error('Failed to get upcoming completions', undefined, error as Error);
      return [];
    }
  }
  
  async getUpcomingPayments(days: number): Promise<{student: Student, program: Program}[]> {
    try {
      const students = await this.getAllStudents();
      const now = new Date();
      const future = new Date();
      future.setDate(future.getDate() + days);
      
      const results: {student: Student, program: Program}[] = [];
      
      students.forEach(student => {
        student.programs.forEach(program => {
          if (program.nextPaymentDate) {
            const paymentDate = new Date(program.nextPaymentDate);
            if (paymentDate >= now && paymentDate <= future) {
              results.push({
                student,
                program,
              });
            }
          }
        });
      });
      
      return results;
    } catch (error) {
      logger.studentService.error('Failed to get upcoming payments', undefined, error as Error);
      return [];
    }
  }

  // Helper methods for compatibility
  async saveStudent(student: Student): Promise<void> {
    if (student.id) {
      await this.updateStudent(student.id, student);
    } else {
      await this.addStudent(student);
    }
  }

  async getAllStudentKeys(): Promise<string[]> {
    try {
      if (universalStorage.isDesktopStorageActive()) {
        const keys = await universalStorage.getItem<string[]>('nalc_student_keys');
        return keys || [];
      }
      
      // Generate from students list
      const students = await this.getAllStudents();
      return students.map(s => s.id.toString());
    } catch (error) {
      logger.studentService.error('Failed to get student keys', undefined, error as Error);
      return [];
    }
  }

  async updateStudentKeys(keys: string[]): Promise<void> {
    try {
      await universalStorage.setItem('nalc_student_keys', keys);
    } catch (error) {
      logger.studentService.error('Failed to update student keys', undefined, error as Error);
    }
  }
}

// Create and export a singleton instance of StudentService
export const studentService = new StudentService();

// Export convenience functions for backward compatibility
export const getStudents = () => studentService.getAllStudents();
export const getStudentById = (id: number) => studentService.getStudentById(id);
export const addStudent = (student: any) => studentService.addStudent(student);
export const updateStudent = (id: number, student: any) => studentService.updateStudent(id, student);
export const deleteStudent = (id: number) => studentService.deleteStudent(id);
export const searchStudents = (query: string) => studentService.searchStudents(query);
